/**
 * Tagalog render vocabulary.
 *
 * Self-registering: importing this module makes the words available to
 * `render(node, 'tl')`. Kept separate from the profile so a parse-only
 * bundle can drop it — see lexicon-registry.ts for why that separation exists.
 */
import type { LanguageLexicon } from '../generators/profiles/types';
import { registerLexicon } from '../lexicon-registry';

export const tagalogLexicon: LanguageLexicon = {
  events: {
    blur: { primary: 'blur' },
    change: { primary: 'change' },
    click: { primary: 'click' },
    dblclick: { primary: 'dblclick' },
    every: { primary: 'bawat' },
    focus: { primary: 'focus' },
    input: { primary: 'input' },
    keydown: { primary: 'keydown' },
    keypress: { primary: 'keypress' },
    keyup: { primary: 'keyup' },
    load: { primary: 'load' },
    mousedown: { primary: 'mousedown' },
    mouseenter: { primary: 'mouseenter' },
    mouseleave: { primary: 'mouseleave' },
    mousemove: { primary: 'mousemove' },
    mouseout: { primary: 'mouseout' },
    mouseover: { primary: 'mouseover' },
    mouseup: { primary: 'mouseup' },
    reset: { primary: 'reset' },
    resize: { primary: 'resize' },
    scroll: { primary: 'scroll' },
    submit: { primary: 'submit' },
    touchcancel: { primary: 'touchcancel' },
    touchend: { primary: 'touchend' },
    touchmove: { primary: 'touchmove' },
    touchstart: { primary: 'touchstart' },
    unload: { primary: 'unload' },
  },
  logical: {
    and: { primary: 'at' },
    changes: { primary: 'nagbabago' },
    contains: { primary: 'naglalaman' },
    else: { primary: 'kung_hindi' },
    empty: { primary: 'walang_laman' },
    end: { primary: 'wakas' },
    equals: { primary: 'katumbas' },
    exists: { primary: 'may' },
    false: { primary: 'mali' },
    has: { primary: 'may' },
    have: { primary: 'may' },
    if: { primary: 'kung' },
    // `includes` takes `contains`' word: both engines read them as one operator,
    // and `kasama` is `inclusive` (pick's range word), which the reader takes it for.
    includes: { primary: 'naglalaman' },
    is: { primary: 'ay' },
    live: { primary: 'live' },
    matches: { primary: 'tumutugma' },
    not: { primary: 'hindi' },
    null: { primary: 'wala' },
    or: { primary: 'o' },
    otherwise: { primary: 'kung_hindi_man' },
    then: { primary: 'pagkatapos' },
    true: { primary: 'totoo' },
    undefined: { primary: 'hindi_tinukoy' },
    when: { primary: 'kapag' },
    where: { primary: 'kung_saan' },
  },
  temporal: {
    forever: { primary: 'magpakailanman' },
    h: { primary: 'h' },
    hour: { primary: 'oras' },
    hours: { primary: 'oras' },
    millisecond: { primary: 'milisegundo' },
    milliseconds: { primary: 'milisegundo' },
    min: { primary: 'min' },
    minute: { primary: 'minuto' },
    minutes: { primary: 'minuto' },
    ms: { primary: 'ms' },
    now: { primary: 'ngayon' },
    s: { primary: 's' },
    second: { primary: 'segundo' },
    seconds: { primary: 'segundo' },
    times: { primary: 'beses' },
  },
  values: {
    body: { primary: 'katawan' },
    detail: { primary: 'detalye' },
    document: { primary: 'dokumento' },
    element: { primary: 'elemento' },
    event: { primary: 'pangyayari' },
    false: { primary: 'mali' },
    it: { primary: 'ito' },
    its: { primary: 'nito' },
    me: { primary: 'ako' },
    my: { primary: 'aking' },
    myself: { primary: 'sarili_ko' },
    null: { primary: 'wala' },
    result: { primary: 'resulta' },
    target: { primary: 'target' },
    true: { primary: 'totoo' },
    undefined: { primary: 'hindi_tinukoy' },
    value: { primary: 'halaga' },
    window: { primary: 'bintana' },
    you: { primary: 'ikaw' },
    your: { primary: 'iyong' },
    yourself: { primary: 'sarili_mo' },
  },
  attributes: {
    attribute: { primary: 'katangian' },
    attributes: { primary: 'mga_katangian' },
    checked: { primary: 'naka_tsek' },
    class: { primary: 'klase' },
    classes: { primary: 'mga_klase' },
    disabled: { primary: 'hindi_pinagana' },
    html: { primary: 'html' },
    id: { primary: 'id' },
    properties: { primary: 'mga_ari_arian' },
    property: { primary: 'ari_arian' },
    style: { primary: 'estilo' },
    styles: { primary: 'mga_estilo' },
    text: { primary: 'teksto' },
    value: { primary: 'halaga' },
  },
  expressions: {
    at: { primary: 'sa' },
    characters: { primary: 'karakter' },
    children: { primary: 'mga_anak' },
    closest: { primary: 'pinakamalapit' },
    empty: { primary: 'walang_laman' },
    'ends with': { primary: 'nagtatapos_sa' },
    exclusive: { primary: 'bukod' },
    first: { primary: 'una' },
    'ignoring case': { primary: 'huwag_pansinin_ang_case' },
    inclusive: { primary: 'kasama' },
    index: { primary: 'indeks' },
    'joined by': { primary: 'pinagsama_sa' },
    last: { primary: 'huli' },
    length: { primary: 'haba' },
    'mapped to': { primary: 'binago_sa' },
    next: { primary: 'susunod' },
    no: { primary: 'walang' },
    parent: { primary: 'magulang' },
    prev: { primary: 'nakaraan' },
    previous: { primary: 'nakaraan' },
    random: { primary: 'random' },
    some: { primary: 'ilan' },
    'sorted by': { primary: 'nakaayos_ayon_sa' },
    'split by': { primary: 'hinati_sa' },
    'starts with': { primary: 'nagsisimula_sa' },
    within: { primary: 'sa_loob_ng' },
  },
};

registerLexicon('tl', tagalogLexicon);
